import { FC } from "react";
import {
  Alert,
  Box,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import __ from "src/utils/Translations";
import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import {
  ImportExportLayoutProps,
  HeaderProps,
  SectionHeaderProps,
  ActionCardProps,
  InstructionsProps,
} from "./types";

const ImportExportLayout: FC<ImportExportLayoutProps> & {
  Header: FC<HeaderProps>;
  SectionHeader: FC<SectionHeaderProps>;
  ActionCard: FC<ActionCardProps>;
  Instructions: FC<InstructionsProps>;
  Divider: FC;
// eslint-disable-next-line react/prop-types
} = ({ children }) => <Box>{children}</Box>;

ImportExportLayout.Header = function Header({
  title,
  description,
  titleSx,
  descriptionSx,
}: HeaderProps) {
  return (
    <>
      <Typography
        variant="h4"
        sx={{ fontWeight: "bold", ...titleSx as object }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ fontSize: "14px", lineHeight: "1.5rem", ...descriptionSx as object }}
        color="text.secondary"
      >
        {description}
      </Typography>
    </>
  );
};

ImportExportLayout.SectionHeader = function SectionHeader({
  title,
  withDivider = false,
}: SectionHeaderProps) {
  return (
    <>
      {withDivider && <Divider sx={{ my: 4 }} />}
      <Typography
        sx={{ fontSize: "20px", fontWeight: 700, lineHeight: "2rem", mt: 4 }}
      >
        {title}
      </Typography>
    </>
  );
};

ImportExportLayout.ActionCard = function ActionCard({
  title,
  description,
  icon = <SaveAltOutlinedIcon color="primary" />,
  actions,
  metadata,
}: ActionCardProps) {
  return (
    <Card
      sx={{
        py: 2,
        px: 3,
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            borderRadius: "50%",
            background: "#F7F8F8",
            width: "52px",
            height: "52px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, textAlign: { xs: "center", sm: "start" } }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: { xs: "center", sm: "start" } }}
          >
            {description}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {actions}
        </Box>
      </Box>

      {metadata && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="body2" color="text.secondary">
              {metadata.label}
            </Typography>
            <Stack sx={{ gap: 1, flexDirection: "row", flexWrap: "wrap", mt: 1 }}>
              {metadata.chips.map((chip) => (
                <Chip key={chip} label={chip} />
              ))}
            </Stack>
          </Box>
        </>
      )}
    </Card>
  );
};

ImportExportLayout.Instructions = function Instructions({
  instructions,
}: InstructionsProps) {
  return (
    <Alert severity="info" sx={{ mt: 2 }}>
      <Typography variant="subtitle1">{__("Instrukcja")}</Typography>
      <ul style={{ paddingLeft: "20px", margin: 0 }}>
        {instructions.map((instruction, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            <Typography variant="body2">{instruction}</Typography>
          </li>
        ))}
      </ul>
    </Alert>
  );
};

ImportExportLayout.Divider = function LayoutDivider() {
  return <Divider sx={{ my: 4 }} />;
};

export default ImportExportLayout;
